import { ChevronV2 } from 'assets/icons/ChevronV2'
import { Input } from 'components/Form'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { BodyText } from 'styling/Typography'
import { SelectOption, SelectOptionsContainer } from './Select.styles'
import { Option } from './Select'

export const AsyncAutocomplete = <T,>({
  loadOptions,
  decoder,
  value,
  onChange,
  label,
  placeholder,
  width,
  inputValue,
  setInputValue,
}: {
  loadOptions: (search: string) => Promise<T[]>
  decoder: (item: T) => Option
  value?: Option | null
  onChange: (value: T) => void
  label?: string
  placeholder?: string
  width?: string
  inputValue: string
  setInputValue: (value: string) => void
}) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const inputRef = useRef<HTMLInputElement>(null)

  const calculatePosition = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const containerHeight = 200
    const containerWidth = 200
    if (!rect)
      return {
        top: 0,
        left: 0,
      }
    const position = {
      top: rect.top + 50,
      left: rect.left,
    }

    if (position.top + containerHeight > windowHeight) {
      return {
        bottom: windowHeight - rect.top + 10,
        left: rect.left,
      }
    }
    if (position.left + containerWidth > windowWidth) {
      position.left = rect.left - containerWidth
    }
    return {
      top: position.top,
      left: position.left,
    }
  }

  useEffect(() => {
    inputRef.current?.addEventListener('focus', () => {
      setOpen(true)
    })
    inputRef.current?.addEventListener('blur', () => {
      setTimeout(() => {
        setOpen(false)
      }, 100)
    })
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        inputRef?.current?.blur()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  // debounce
  useEffect(() => {
    const timeout = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await loadOptions(inputValue || undefined)
        setOptions(data)
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeout)
  }, [inputValue])

  return (
    <Input
      width={width}
      label={label}
      placeholder={placeholder}
      ref={inputRef}
      value={value?.label || inputValue}
      onChange={(e) => {
        setInputValue(e.target.value)
      }}
    >
      {open &&
        createPortal(
          <SelectOptionsContainer
            style={{
              ...calculatePosition(),
            }}
            ref={dropdownRef}
          >
            {loading ? (
              <SelectOption>
                <BodyText>Loading...</BodyText>
              </SelectOption>
            ) : (
              options.map((o) => {
                const option = decoder(o)
                return (
                  <SelectOption
                    key={option.value}
                    active={option.value.toString() === value?.value?.toString()}
                    onMouseDown={() => {
                      onChange(o)

                      setOpen(false)
                      inputRef?.current?.blur()
                    }}
                  >
                    {option.label}
                  </SelectOption>
                )
              })
            )}
          </SelectOptionsContainer>,
          document.body,
        )}
    </Input>
  )
}

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  height: 1.5rem;
  color: ${(props) => props.theme.textColor};
`
